import axios from "axios";
import { API_URL } from "@/config";
import { isSet } from "@/utils/value";
import { useSubscribeForFile } from "@/utils/useSubscribeForFile";

const store = {
  namespaced: true,

  actions: {
    async getMany(_, criteria) {
      const res = await axios.get(`${API_URL}/transactions`, { params: criteria });

      if (!res.data || typeof res.data.items !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getOne(_, transactionId) {
      const res = await axios.get(`${API_URL}/transactions/${transactionId}`);

      if (!res.data || typeof res.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getUserBalance(_, userId) {
      const res = await axios.get(`${API_URL}/transactions/user/${userId}/balance`);

      if (!res.data || typeof res.data.amount !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getUserEndOfMonthBalance(_, userId) {
      const res = await axios.get(`${API_URL}/transactions/user/${userId}/endOfMonthBalance`);

      if (!res.data || typeof res.data.amount !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getUserMonthlySummaries(_, userId) {
      const res = await axios.get(`${API_URL}/transactions/user/${userId}/monthlySummaries`);

      if (!res.data || typeof res.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getUserInsufficientFundsForTomorrow(_, userId) {
      const res = await axios.get(`${API_URL}/transactions/user/${userId}/insufficientFunds`);

      if (!res.data || typeof res.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getUsersTransactions(_, criteria) {
      if (isSet(criteria?.fiscalized)) {
        criteria.fiscalized = +criteria.fiscalized;
      }

      const res = await axios.get(`${API_URL}/transactions/users`, { params: criteria });

      if (!res.data || typeof res.data?.usersTransactions?.items !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getDeposits(_, criteria) {
      if (isSet(criteria?.fiscalized)) {
        criteria.fiscalized = +criteria.fiscalized;
      }

      const res = await axios.get(`${API_URL}/transactions/deposits`, { params: criteria });

      if (!res.data || typeof res.data.items !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async createForUser(_, { userId, transactionData }) {
      const res = await axios.post(`${API_URL}/transactions/user/${userId}`, transactionData);

      if (!res.data || typeof res.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async createForUsers(_, { userIds, transactionData }) {
      let data = {
        ...transactionData,
        userIds: userIds
          .map((userId) => (typeof userId === "object" ? userId.id : userId))
          .join(","),
      };

      const res = await axios.post(`${API_URL}/transactions/users/transactions`, data);

      if (!res.data || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async update(_, { transactionId, transactionData }) {
      const res = await axios.put(`${API_URL}/transactions/${transactionId}`, transactionData);

      if (!res.data || typeof res.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async remove(_, transactionId) {
      const res = await axios.delete(`${API_URL}/transactions/${transactionId}`);

      if (!res.data || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async pay(_, paymentData) {
      const res = await axios.post(`${API_URL}/payments/pay`, paymentData);

      if (!res.data || typeof res.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getUserTransactionsWithBalance(_, { userId, criteria = {} }) {
      const res = await axios.get(`${API_URL}/transactions/${userId}/withBalance`, {
        params: criteria,
      });

      if (!res.data || typeof res.data.items !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async monthlySummariesExport({ rootGetters, commit }, userId) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("transactions/generateMonthlySummariesExport", {
        url: `${API_URL}/transactions/${userId}/monthlySummaries/export`,
        method: "POST",
      });
    },

    async userTransactionsWithBalanceExport({ rootGetters, commit }, { userId, criteria = {} }) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("transactions/generateUserTransactionsWithBalanceExport", {
        url: `${API_URL}/transactions/${userId}/withBalance/export`,
        method: "POST",
        data: criteria,
      });
    },

    async fiscalize(_, transactionId) {
      const res = await axios.post(`${API_URL}/transactions/${transactionId}/fiscalize`);

      if (!res.data || typeof res.data !== "object" || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data.status;
    },

    async bulkFiscalize(_, data) {
      const res = await axios.post(`${API_URL}/transactions/bulkFiscalize`, {
        transactionsIds: data,
      });

      if (!res.data || typeof res.data !== "object" || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data.status;
    },

    async bulkFiscalizeAll(_, criteria) {
      const res = await axios.post(`${API_URL}/transactions/fiscalizeAll`, criteria);

      if (!res.data || typeof res.data !== "object" || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data.status;
    },

    async getUsersWithTransactions(_, criteria) {
      const res = await axios.get(`${API_URL}/transactions/usersWithTransactions`, {
        params: criteria,
      });

      if (!res.data || typeof res.data?.usersWithTransactions?.items !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async generateUsersWithTransactionsExport({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/balanceUserReport", {
        url: `${API_URL}/reports/balanceUserReport`,
        method: "POST",
        data: criteria,
      });
    },

    async getTotalDepositsAmount(_, criteria) {
      const res = await axios.get(`${API_URL}/transactions/totalDeposits`, {
        params: criteria,
      });

      if (res.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getTotalAmounts(_, criteria) {
      if (isSet(criteria?.fiscalized)) {
        criteria.fiscalized = +criteria.fiscalized;
      }

      const res = await axios.get(`${API_URL}/transactions/totalAmounts`, {
        params: criteria,
      });

      if (res.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getImportedPaymentsFiles(_, criteria) {
      const res = await axios.get(`${API_URL}/transactions/importedPaymentsFiles`, {
        params: criteria,
      });

      if (res.status !== 200 || !res.data || !res.data.items) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getImportedPaymentsFile(_, importedPaymentsFileId) {
      const res = await axios.get(
        `${API_URL}/transactions/importedPaymentsFiles/${importedPaymentsFileId}`,
      );

      if (res.status !== 200 || !res.data || !res.data.id) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getImportedPaymentsFilePayments(_, params) {
      const { importedPaymentsFileId, ...criteria } = params;
      const res = await axios.get(
        `${API_URL}/transactions/importedPaymentsFiles/${importedPaymentsFileId}/payments`,
        { params: criteria },
      );

      if (res.status !== 200 || !res.data || !res.data.items) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async updateImportedPaymentMatchedUser(_, { importedPaymentId, userId }) {
      const res = await axios.put(
        `${API_URL}/transactions/importedPayments/${importedPaymentId}/updateMatchedUser`,
        {
          userId,
        },
      );

      if (res.status !== 200 || !res.data || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async updateImportedPaymentMatchedChild(_, { importedPaymentId, childId }) {
      const res = await axios.put(
        `${API_URL}/transactions/importedPayments/${importedPaymentId}/updateMatchedChild`,
        {
          childId,
        },
      );

      if (res.status !== 200 || !res.data || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async clearImportedPaymentMatchedUser(_, importedPaymentId) {
      const res = await axios.put(
        `${API_URL}/transactions/importedPayments/${importedPaymentId}/clearMatchedUser`,
      );

      if (res.status !== 200 || !res.data || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async clearImportedPaymentMatchedChild(_, importedPaymentId) {
      const res = await axios.put(
        `${API_URL}/transactions/importedPayments/${importedPaymentId}/clearMatchedChild`,
      );

      if (res.status !== 200 || !res.data || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async approveImportedPayment(_, importedPaymentId) {
      const res = await axios.put(
        `${API_URL}/transactions/importedPayments/${importedPaymentId}/approve`,
      );

      if (res.status !== 200 || !res.data || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async confirmImportedPaymentsFile(_, importedPaymentsFileId) {
      const res = await axios.post(
        `${API_URL}/transactions/importedPaymentsFiles/${importedPaymentsFileId}/confirm`,
      );

      if (res.status !== 200 || !res.data || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async getParentPerChildrenBalance(_, parentId) {
      const res = await axios.get(`${API_URL}/transactions/parent/${parentId}/perChildrenBalance`);

      if (res.status !== 200 || !res.data) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },
  },
};

export default store;
